import { useCallback, useState } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging.ts";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { wait } from "@entrilia/shared/utilities/promiseHelper.ts";
import biApi from "../../../../../../api/biApi.ts";
import type { BuildBalanceSheetReportRequest, DimensionDescriptor } from "../../../../../../api/types/svTypes.ts";
import { buildBalanceSheetExportRequest } from "../utils/buildBalanceSheetExportRequest.ts";

const maxExportReadyAttempts = 200;
const exportReadyPollDelayMs = 1500;

export const useBalanceSheetExport = (
  request: BuildBalanceSheetReportRequest,
  dimensions: DimensionDescriptor[],
  exportValuesAsDisplayed: boolean
) => {
  const [exporting, setExporting] = useState(false);
  const { sendNotificationError } = useNotificationContext();

  const downloadExport = useCallback(async (token: string) => {
    await downloadFileFromUrl(biApi.generateExportDownloadLink(token));
  }, []);

  const waitExportReady = useCallback(
    async (token: string): Promise<void> => {
      try {
        for (let attempt = 0; attempt <= maxExportReadyAttempts; attempt += 1) {
          const response = await biApi.checkExportReady(token);
          if (!response.success || response.data.error) {
            sendNotificationError("An error occurred while exporting the file. Please try again.");
            setExporting(false);
            return;
          }

          if (response.data.fileProcessed) {
            await downloadExport(token);
            setExporting(false);
            return;
          }

          if (attempt < maxExportReadyAttempts) {
            await wait(exportReadyPollDelayMs);
          }
        }
        sendNotificationError("An error occurred while generating an export file. Please try again.");
        setExporting(false);
      } catch (error) {
        logError(error, "[useBalanceSheetExport] checkExportReady");
        sendNotificationError("An error occurred while exporting the file. Please try again.");
        setExporting(false);
      }
    },
    [downloadExport, sendNotificationError]
  );

  const requestExport = useCallback(
    (withParameters: boolean) => {
      setExporting(true);
      biApi
        .requestBuildBalanceSheetExport(
          buildBalanceSheetExportRequest(request, dimensions, withParameters, exportValuesAsDisplayed)
        )
        .then((response) => {
          if (!response.success) {
            sendNotificationError("An error occurred while exporting the file. Please try again.");
            setExporting(false);
            return;
          }

          void waitExportReady(response.data.token);
        })
        .catch(() => {
          sendNotificationError("An error occurred while exporting the file. Please try again.");
          setExporting(false);
        });
    },
    [dimensions, request, exportValuesAsDisplayed, sendNotificationError, waitExportReady]
  );

  return {
    exporting,
    requestExport,
    requestExportWithParameters: () => requestExport(true),
    requestExportWithoutParameters: () => requestExport(false),
  };
};
